/**
 * How a seller delivers: by hand (zones), shipping, a pickup point, or wherever they agree. Each
 * option's details stay disabled until the option is ticked, and the days-and-hours grid has one
 * window per weekday (Mon 9–18, Tue–Thu 10–12, Fri off, Sat 9–17…).
 */
import { type DeliveryOptions, type MeetingSlot } from "../../shared/market";
import { RATED_ZONES, ZONE_LABEL, type RatedZone } from "../../shared/shipping";
import { WEEKDAY_LABEL } from "../../shared/market";
import { useI18n } from "../i18n/I18nProvider";

/** Monday first, as people read a week. */
const WEEK = [1, 2, 3, 4, 5, 6, 0];

export interface DeliveryDraft {
  handOn: boolean;
  zones: RatedZone[];
  areas: string;
  handCost: string;
  shipOn: boolean;
  carriers: string;
  shipCost: string;
  pickupOn: boolean;
  pickupAddress: string;
  pickupHours: string;
  arrange: boolean;
  /** Per weekday: open that day, and its window. */
  days: Record<number, { on: boolean; from: string; to: string }>;
}

export function draftFrom(d: DeliveryOptions): DeliveryDraft {
  const slot = (day: number) => d.schedule?.slots.find((s) => s.day === day);
  return {
    handOn: Boolean(d.hand),
    zones: d.hand?.zones ?? [],
    areas: d.hand?.areas ?? "",
    handCost: String(d.hand?.costArs ?? 0),
    shipOn: Boolean(d.ship),
    carriers: d.ship?.carriers ?? "",
    shipCost: d.ship?.costArs == null ? "" : String(d.ship.costArs),
    pickupOn: Boolean(d.pickup),
    pickupAddress: d.pickup?.address ?? "",
    pickupHours: d.pickup?.hours ?? "",
    arrange: d.arrange,
    days: Object.fromEntries(WEEK.map((day) => [day, { on: Boolean(slot(day)), from: slot(day)?.from ?? "10:00", to: slot(day)?.to ?? "18:00" }])),
  };
}

const num = (v: string) => (v.trim() === "" ? null : Number(v.replace(/[.\s$]/g, "")));

/** What the API gets: unticked options are null; the details of ticked ones go as typed (the server checks them). */
export function deliveryPayload(d: DeliveryDraft) {
  const slots: MeetingSlot[] = WEEK.filter((day) => d.days[day]?.on).map((day) => ({ day, from: d.days[day].from, to: d.days[day].to }));
  return {
    hand: d.handOn ? { zones: d.zones, areas: d.areas.trim(), costArs: num(d.handCost) ?? 0 } : null,
    ship: d.shipOn ? { carriers: d.carriers.trim(), costArs: num(d.shipCost) } : null,
    pickup: d.pickupOn ? { address: d.pickupAddress.trim(), hours: d.pickupHours.trim() } : null,
    arrange: d.arrange,
    schedule: slots.length ? { slots } : null,
  };
}

export function DeliveryFieldset({ value: d, onChange, errors }: { value: DeliveryDraft; onChange: (d: DeliveryDraft) => void; errors: string[] }) {
  const { t, locale } = useI18n();
  const set = (patch: Partial<DeliveryDraft>) => onChange({ ...d, ...patch });
  const setDay = (day: number, patch: Partial<DeliveryDraft["days"][number]>) => set({ days: { ...d.days, [day]: { ...d.days[day], ...patch } } });
  const err = (key: string) =>
    errors.includes(key) ? (
      <p className="field-error" role="alert">
        {t(`mkt.set.err.${key.split(".")[1]}` as never)}
      </p>
    ) : null;
  const firstOn = WEEK.find((day) => d.days[day].on);

  return (
    <fieldset className="stack delivery-settings">
      <legend>{t("mkt.set.delivery")}</legend>

      <div className={d.handOn ? "delivery-option is-on" : "delivery-option"}>
        <label className="check">
          <input type="checkbox" checked={d.handOn} onChange={(e) => set({ handOn: e.target.checked })} /> {t("mkt.set.hand")}
        </label>
        <fieldset className="delivery-details" disabled={!d.handOn}>
          <div className="check-row" role="group" aria-label={t("mkt.set.handZones")}>
            {RATED_ZONES.map((z) => (
              <label key={z} className="check">
                <input type="checkbox" checked={d.zones.includes(z)} onChange={(e) => set({ zones: e.target.checked ? [...d.zones, z] : d.zones.filter((x) => x !== z) })} />{" "}
                {ZONE_LABEL[z][locale]}
              </label>
            ))}
          </div>
          <div className="field-row">
            <div className="field grow">
              <label htmlFor="ds-areas">{t("mkt.set.handAreas")}</label>
              <input id="ds-areas" value={d.areas} onChange={(e) => set({ areas: e.target.value })} placeholder={t("mkt.set.handAreasHint")} maxLength={200} />
            </div>
            <div className="field">
              <label htmlFor="ds-handcost">{t("mkt.set.cost")}</label>
              <input id="ds-handcost" inputMode="numeric" value={d.handCost} onChange={(e) => set({ handCost: e.target.value })} />
            </div>
          </div>
        </fieldset>
        {err("delivery.hand")}
      </div>

      <div className={d.shipOn ? "delivery-option is-on" : "delivery-option"}>
        <label className="check">
          <input type="checkbox" checked={d.shipOn} onChange={(e) => set({ shipOn: e.target.checked })} /> {t("mkt.set.ship")}
        </label>
        <fieldset className="delivery-details field-row" disabled={!d.shipOn}>
          <div className="field grow">
            <label htmlFor="ds-carriers">{t("mkt.set.carriers")}</label>
            <input id="ds-carriers" value={d.carriers} onChange={(e) => set({ carriers: e.target.value })} placeholder="Correo Argentino, Andreani…" maxLength={120} />
          </div>
          <div className="field">
            <label htmlFor="ds-shipcost">{t("mkt.set.cost")}</label>
            <input id="ds-shipcost" inputMode="numeric" value={d.shipCost} onChange={(e) => set({ shipCost: e.target.value })} placeholder={t("mkt.quoted")} />
          </div>
        </fieldset>
      </div>

      <div className={d.pickupOn ? "delivery-option is-on" : "delivery-option"}>
        <label className="check">
          <input type="checkbox" checked={d.pickupOn} onChange={(e) => set({ pickupOn: e.target.checked })} /> {t("mkt.set.pickup")}
        </label>
        <fieldset className="delivery-details" disabled={!d.pickupOn}>
          <div className="field-row">
            <div className="field grow">
              <label htmlFor="ds-pickup">{t("mkt.set.pickupAddress")}</label>
              <input id="ds-pickup" value={d.pickupAddress} onChange={(e) => set({ pickupAddress: e.target.value })} maxLength={200} />
            </div>
            <div className="field">
              <label htmlFor="ds-hours">{t("mkt.set.pickupHours")}</label>
              <input id="ds-hours" value={d.pickupHours} onChange={(e) => set({ pickupHours: e.target.value })} maxLength={120} />
            </div>
          </div>
          <p className="muted small">{t("mkt.set.pickupHint")}</p>
        </fieldset>
        {err("delivery.pickup")}
      </div>

      <div className={d.arrange ? "delivery-option is-on" : "delivery-option"}>
        <label className="check">
          <input type="checkbox" checked={d.arrange} onChange={(e) => set({ arrange: e.target.checked })} /> {t("mkt.set.arrange")}
        </label>
        {err("delivery.arrange")}
      </div>

      <div className="delivery-option">
        <h3 className="small-title">{t("mkt.set.schedule")}</h3>
        <p className="muted small">{t("mkt.set.scheduleHint")}</p>
        <table className="schedule-grid">
          <caption className="sr-only">{t("mkt.set.scheduleDays")}</caption>
          <tbody>
            {WEEK.map((day) => {
              const v = d.days[day];
              const name = WEEKDAY_LABEL[day][locale];
              const badWindow = v.on && !(v.from && v.to && v.from < v.to);
              return (
                <tr key={day} className={v.on ? "is-on" : undefined}>
                  <th scope="row">
                    <label className="check">
                      <input type="checkbox" checked={v.on} onChange={(e) => setDay(day, { on: e.target.checked })} /> {name}
                    </label>
                  </th>
                  <td>
                    <input type="time" step={900} aria-label={t("mkt.set.fromDay", { day: name })} value={v.from} disabled={!v.on} onChange={(e) => setDay(day, { from: e.target.value })} />
                  </td>
                  <td aria-hidden="true">–</td>
                  <td>
                    <input type="time" step={900} aria-label={t("mkt.set.toDay", { day: name })} value={v.to} disabled={!v.on} onChange={(e) => setDay(day, { to: e.target.value })} />
                  </td>
                  <td>{badWindow ? <span className="field-error small">{t("mkt.set.err.window")}</span> : !v.on ? <span className="muted small">{t("mkt.set.dayOff")}</span> : null}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {firstOn !== undefined && (
          <button
            type="button"
            className="btn btn-link btn-sm"
            onClick={() => set({ days: Object.fromEntries(WEEK.map((day) => [day, d.days[day].on ? { ...d.days[day], from: d.days[firstOn].from, to: d.days[firstOn].to } : d.days[day]])) })}
          >
            {t("mkt.set.copyHours", { day: WEEKDAY_LABEL[firstOn][locale] })}
          </button>
        )}
        {err("delivery.schedule")}
      </div>
    </fieldset>
  );
}
